import { useMemo } from 'react';
import { Euler, Vector3, Quaternion, Plane } from 'three';

import {
    removeDuplicates,
    sortVerticesInPlane
} from 'app/modules/wallforming/components/scenes/objects/dimensions/utils/dimensionUtils';
import { DIMENSION_COLOR } from 'shared/constants';

import type { DimensionText } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionText';
import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';

interface CreateFaceDimensioningProps {
    selectedPoiIndex: number | undefined;
    workingPlane: Plane | null;
    buildingColliders: ColliderPoint[];
    dir: Vector3;
    mid: Vector3;
}

const Z_SCALE = 0.4;

export const useCreateFaceDimensioning = ({
    selectedPoiIndex,
    workingPlane,
    buildingColliders,
    dir,
    mid
}: CreateFaceDimensioningProps) => {
    return useMemo(() => {
        const defaultReturn = {
            positions: [] as number[],
            texts: [] as DimensionText[],
            euler: new Euler(),
            floorZ: 0
        };

        if (!workingPlane || selectedPoiIndex === undefined || !buildingColliders) {
            return defaultReturn;
        }

        const planeNormal = workingPlane.normal.clone();
        const texts: DimensionText[] = [];
        const buildingPoints = buildingColliders.filter((p) => p.poiIndex === selectedPoiIndex);

        if (buildingPoints.length < 2) {
            return defaultReturn;
        }

        const floorZ = Math.min(...buildingPoints.map((c) => c.position.z));

        const euler = new Euler();
        if (buildingPoints.length >= 3) {
            const verts = buildingPoints.map((p) => p.position);
            const textNormal = verts[1].clone().sub(verts[0]).cross(verts[2].clone().sub(verts[0])).normalize();

            if (textNormal.lengthSq() > 0) {
                const quat = new Quaternion()
                    .setFromUnitVectors(new Vector3(0, 0, 1), textNormal)
                    .multiply(new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), dir.clone().negate()));

                const textUp = new Vector3(0, 0, 1).applyQuaternion(quat);
                if (textUp.dot(textNormal) < 0) {
                    quat.multiply(new Quaternion().setFromAxisAngle(dir.clone().negate(), Math.PI));
                }

                euler.setFromQuaternion(quat);
            }
        }

        const zOffset = floorZ - 0.3;
        const verts = sortVerticesInPlane(removeDuplicates(buildingPoints.map((p) => p.position.clone())), planeNormal);

        if (verts.length < 3) {
            return defaultReturn;
        }

        const center = verts.reduce((a, v) => a.add(v), new Vector3()).multiplyScalar(1 / verts.length);
        const width = verts[0].distanceTo(verts[1]);
        const height = verts[1].distanceTo(verts[2]);

        // update linesegments positions
        const positions: number[] = [];
        for (let i = 0; i < verts.length; i++) {
            const a = verts[i].clone().setZ(zOffset - (verts[i].z - zOffset) * Z_SCALE);
            const b = verts[(i + 1) % verts.length]
                .clone()
                .setZ(zOffset - (verts[(i + 1) % verts.length].z - zOffset) * Z_SCALE);
            positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
        }

        // update wall measures texts
        const labelWidth = `${height.toFixed(2)}m`;
        const labelText = `${width.toFixed(2)}m`;
        const fontSize = 0.1;
        const verticalOffsetFactor = fontSize * 2.8;
        const horizontalOffsetFactor = fontSize * 2;
        const zAdjusted = zOffset - (center.z - zOffset) * Z_SCALE;

        const dirLeft = verts[1].clone().sub(center).normalize().multiplyScalar(horizontalOffsetFactor);
        const dirRight = verts[2].clone().sub(center).normalize().multiplyScalar(horizontalOffsetFactor);

        texts.push(
            {
                id: verts[1].toArray().toString(),
                position: verts[1].clone().add(dirLeft).setZ(zAdjusted).sub(mid),
                text: labelText,
                fontSize,
                color: DIMENSION_COLOR
            },
            {
                id: verts[2].toArray().toString(),
                position: verts[2].clone().add(dirRight).setZ(zAdjusted).sub(mid),
                text: labelText,
                fontSize,
                color: DIMENSION_COLOR
            },
            {
                id: center.toArray().toString(),
                position: center
                    .clone()
                    .setZ(zAdjusted)
                    .sub(mid)
                    .add(new Vector3(0, 0, verticalOffsetFactor)),
                text: labelWidth,
                fontSize,
                color: DIMENSION_COLOR
            }
        );

        return { positions, texts, euler, mid, floorZ };
    }, [selectedPoiIndex, workingPlane, buildingColliders, dir, mid]);
};
